import produce from "immer";
import { difference, differenceBy, unionBy } from "lodash";
import moment from "moment";
import { DateRange } from "@sonder/ui/source/styled/components/DateRangePicker";
import { SCHEDULE_SAVED_FILTERS } from "core/constants";
import {
  GetTaskSchedulesPlace,
  GetTaskSchedulesRelatedBooking,
  GetTaskSchedulesRelatedTask,
} from "core/queries/GetTaskSchedules";
import {
  ActionType,
  AddBatchJobAction,
  AddSequentialJobAction,
  CloseItemAction,
  DeselectTasksAction,
  RemoveBatchJobAction,
  RemoveSequentialJobAction,
  SelectTasksAction,
  SequentialJobActionPayloadWithId,
  SetArchivedTask,
  SetCompletingTaskAction,
  SetDisabledAction,
  SetFiltersAction,
  SetHoveringTask,
  SetModifyingTask,
  SetPrepKitModalStateAction,
  SetSelectingAction,
  SetUnscheduledTask,
  UnsetFiltersAction,
  UpdateDateRangeAction,
  ViewItemAction,
} from "schedule/actions";
import { diffStatusFilters, unionStatusFilters } from "tasks/helpers";
import {
  CleaningRequestCategory,
  FilterStatus,
  TaskScheduleStatusEnum,
} from "typings/globalGQLTypes";

export interface ViewedItem {
  boundingRect: Omit<DOMRect, "toJSON">;
  place?: GetTaskSchedulesPlace;
  booking?: GetTaskSchedulesRelatedBooking;
  task?: GetTaskSchedulesRelatedTask;
}

export interface SelectedTaskDetails {
  id: string;
  cleaningCompanyId: string;
  internalRef: string;
  type: string;
}

export interface ScheduleFilterInput {
  cleaningCompanyIds?: string[] | null;
  cleaningRequestCategory?: CleaningRequestCategory[] | null;
  sameDayCleansOnly?: boolean | null;
  scheduleStatus?: TaskScheduleStatusEnum;
  status?: FilterStatus[] | null;
}

export interface ScheduleState {
  archivedTaskId: string;
  batchJobs: string[];
  dateRange: DateRange;
  filters: ScheduleFilterInput;
  isDisabled: boolean;
  isSelecting: boolean;
  completingTaskId: string;
  hoveringTaskId: string;
  modifyingTaskId: string;
  selectedTasks: SelectedTaskDetails[];
  sequentialJobs: SequentialJobActionPayloadWithId[];
  unscheduledTaskId: string;
  viewedItems: ViewedItem[];
  isPrepKitReferenceOpen: boolean;
}

export type AllScheduleActions =
  | AddBatchJobAction
  | AddSequentialJobAction
  | CloseItemAction
  | DeselectTasksAction
  | RemoveBatchJobAction
  | RemoveSequentialJobAction
  | SelectTasksAction
  | SetArchivedTask
  | SetDisabledAction
  | SetFiltersAction
  | SetCompletingTaskAction
  | SetHoveringTask
  | SetModifyingTask
  | SetSelectingAction
  | SetUnscheduledTask
  | UnsetFiltersAction
  | UpdateDateRangeAction
  | ViewItemAction
  | SetPrepKitModalStateAction;

export interface FilterPair {
  globalFilters: ScheduleFilterInput;
  localFilters: ScheduleFilterInput;
}

export const getTodayDateRange = (date = moment()) => ({
  endAt: date.clone().hours(20).minutes(59).seconds(59),
  startAt: date.clone().hours(8).minutes(0).seconds(0),
});

export const initialState: ScheduleState = {
  batchJobs: [],
  dateRange: getTodayDateRange(),
  filters: {
    cleaningCompanyIds: [],
    cleaningRequestCategory: [],
    sameDayCleansOnly: false,
    scheduleStatus: TaskScheduleStatusEnum.all,
    status: [
      FilterStatus.complete,
      FilterStatus.incomplete,
      FilterStatus.inprogress,
    ],
  },
  isDisabled: false,
  isSelecting: false,
  archivedTaskId: "",
  completingTaskId: "",
  hoveringTaskId: "",
  modifyingTaskId: "",
  selectedTasks: [],
  sequentialJobs: [],
  unscheduledTaskId: "",
  viewedItems: [],
  isPrepKitReferenceOpen: false,
};

export const savedFilterState = {
  ...initialState,
  filters: {
    ...initialState.filters,
    ...JSON.parse(localStorage.getItem(SCHEDULE_SAVED_FILTERS) || "{}"),
  },
};

export const getViewItemId = (viewedItem: ViewedItem) => {
  return viewedItem.task?.id || viewedItem.booking?.id;
};

const saveFilters = (filters: ScheduleFilterInput) => {
  localStorage.setItem(SCHEDULE_SAVED_FILTERS, JSON.stringify(filters));
};

export const scheduleReducer = (
  state = savedFilterState,
  action: AllScheduleActions,
) =>
  produce(state, draft => {
    switch (action.type) {
      case ActionType.SetDisabled: {
        draft.isDisabled = action.payload.isDisabled;
        return draft;
      }

      case ActionType.SetSelecting: {
        draft.isSelecting = action.payload.isSelecting;
        return draft;
      }

      case ActionType.UpdateDateRange: {
        draft.dateRange = action.payload.dateRange;
        return draft;
      }

      case ActionType.SetPrepKitModalState: {
        draft.isPrepKitReferenceOpen = action.payload.isPrepKitReferenceOpen;
        return draft;
      }

      case ActionType.ViewItem: {
        draft.viewedItems = unionBy(
          draft.viewedItems,
          action.payload.items,
          getViewItemId,
        );
        return draft;
      }

      case ActionType.CloseItem: {
        draft.viewedItems = differenceBy(
          draft.viewedItems,
          action.payload.items,
          getViewItemId,
        );
        return draft;
      }

      case ActionType.SelectTasks: {
        const tasks = action.payload.tasks;
        draft.selectedTasks = unionBy(draft.selectedTasks, tasks, "id");
        return draft;
      }

      case ActionType.DeselectTasks: {
        const tasks = action.payload.tasks;
        draft.selectedTasks = differenceBy(draft.selectedTasks, tasks, "id");
        return draft;
      }

      case ActionType.AddBatchJobAction: {
        const {
          payload: { jobId },
        } = action;
        draft.batchJobs.push(jobId);
        return draft;
      }

      case ActionType.RemoveBatchJobAction: {
        const {
          payload: { jobId },
        } = action;
        draft.batchJobs = difference(draft.batchJobs, [jobId]);
        return draft;
      }

      case ActionType.AddSequentialJobAction: {
        const {
          payload: { tasks, variables },
        } = action;
        draft.sequentialJobs.push({
          jobId: new Date().getTime().toString(),
          tasks,
          variables,
        });
        return draft;
      }

      case ActionType.RemoveSequentialJobAction: {
        const {
          payload: { jobId },
        } = action;
        draft.sequentialJobs = draft.sequentialJobs.filter(
          job => job.jobId !== jobId,
        );
        return draft;
      }

      case ActionType.SetArchivedTask: {
        const {
          payload: { taskId },
        } = action;
        draft.archivedTaskId = taskId;
        return draft;
      }

      case ActionType.SetUnscheduledTask: {
        const {
          payload: { taskId },
        } = action;
        draft.unscheduledTaskId = taskId;
        return draft;
      }

      case ActionType.SetCompletingTask: {
        const {
          payload: { taskId },
        } = action;
        draft.completingTaskId = taskId;
        return draft;
      }

      case ActionType.SetHoveringTask: {
        const {
          payload: { taskId },
        } = action;
        draft.hoveringTaskId = taskId;
        return draft;
      }

      case ActionType.SetModifyingTask: {
        const {
          payload: { taskId },
        } = action;
        draft.modifyingTaskId = taskId;
        return draft;
      }

      case ActionType.SetFilters: {
        draft.filters = {
          ...draft.filters,
          ...action.payload,
          ...unionStatusFilters({
            globalFilters: draft.filters,
            localFilters: action.payload,
          }),
        };
        saveFilters(draft.filters);
        return draft;
      }

      case ActionType.UnsetFilters: {
        draft.filters = {
          ...draft.filters,
          ...action.payload,
          ...diffStatusFilters({
            globalFilters: draft.filters,
            localFilters: action.payload,
          }),
        };
        saveFilters(draft.filters);
        return draft;
      }

      default:
        return draft;
    }
  });
